import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import AnimatedCounter from "@/components/AnimatedCounter";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import {
  ArrowRight, Sparkles, MapPin, BarChart3, Calendar as CalendarIcon,
  MessageSquare, Compass, Check, Star, Zap, ShieldCheck, TrendingUp, Quote, Globe,
} from "lucide-react";

const RESTAURANT = "https://images.unsplash.com/photo-1685040235380-a42a129ade4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjByZXN0YXVyYW50JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc4NDE2NTYxfDA&ixlib=rb-4.1.0&q=85";
const SALON = "https://images.pexels.com/photos/7195799/pexels-photo-7195799.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
// Four distinct portraits, one per testimonial (Pexels — natural, non-stocky)
const TESTI_SOPHIE = "https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&cs=tinysrgb&w=160&h=160&fit=crop";
const TESTI_KARIM = "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=160&h=160&fit=crop";
const TESTI_CAMILLE = "https://images.pexels.com/photos/762020/pexels-photo-762020.jpeg?auto=compress&cs=tinysrgb&w=160&h=160&fit=crop";
const TESTI_ANTOINE = "https://images.pexels.com/photos/91227/pexels-photo-91227.jpeg?auto=compress&cs=tinysrgb&w=160&h=160&fit=crop";

const FEATURES = [
  { icon: Compass, title: "Présence locale", text: "Optimisez votre fiche Google Business, votre site et vos réseaux pour être trouvé par vos voisins." },
  { icon: MapPin, title: "Référencement local", text: "Grimpez dans le pack local de Google Maps et apparaissez dans les recherches qui comptent." },
  { icon: Sparkles, title: "Studio de contenu", text: "Légendes Instagram, posts Google Business, descriptions et réponses aux avis — rédigés en quelques secondes." },
  { icon: BarChart3, title: "Audit de visibilité", text: "Diagnostic en 60 secondes, recommandations priorisées et benchmark face à vos concurrents." },
  { icon: CalendarIcon, title: "Calendrier éditorial", text: "Planifiez votre semaine, identifiez vos meilleurs créneaux et publiez avec sérénité." },
  { icon: MessageSquare, title: "Réputation", text: "Répondez à tous vos avis Google d'un ton humain et cohérent — sans y passer vos soirées." },
];

const SECTORS = ["Restaurants", "Salons", "Cafés", "Spas", "Barbiers", "Boutiques", "Freelances", "Coworkings", "Boulangeries", "Studios"];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-4 z-50 px-4">
        <div className="max-w-6xl mx-auto glass-strong rounded-full border border-border h-14 flex items-center justify-between px-3 sm:px-4">
          <Logo size={20} />
          <nav className="hidden md:flex items-center gap-7 text-sm text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors">Plateforme</a>
            <a href="#pricing" className="hover:text-foreground transition-colors">Tarifs</a>
            <a href="#testimonials" className="hover:text-foreground transition-colors">Clients</a>
            <a href="#faq" className="hover:text-foreground transition-colors">FAQ</a>
          </nav>
          <div className="flex items-center gap-1.5">
            <ThemeToggle />
            <Link to="/login" className="hidden sm:block"><Button variant="ghost" size="sm" className="rounded-full" data-testid="nav-login-btn">Connexion</Button></Link>
            <Link to="/signup"><Button size="sm" className="rounded-full btn-glow" data-testid="nav-signup-btn">Commencer<ArrowRight className="ml-1 h-3.5 w-3.5" /></Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden pt-16 sm:pt-24 pb-32">
        <div className="aurora" />
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 glass rounded-full text-xs font-medium fade-up">
            <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-primary to-violet-500 pulse-soft" />
            <span className="text-muted-foreground">La plateforme de croissance des commerces locaux</span>
          </div>

          <h1 className="mt-8 font-display font-black tracking-tighter leading-[1.15] fade-up-1
                         text-5xl sm:text-6xl lg:text-8xl">
            Devenez le commerce<br className="hidden sm:block" />
            <span className="text-gradient">qu’on recommande</span><br className="hidden sm:block" />
            <span className="font-serif-italic font-normal text-foreground/80">dans votre quartier.</span>
          </h1>

          <p className="mt-7 text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed fade-up-2">
            Local Pulse est le système d'exploitation des commerces locaux : présence en ligne, fiche Google, réseaux sociaux et avis clients —
            <em className="font-serif-italic text-foreground"> centralisés et optimisés</em> pour faire grandir votre chiffre d'affaires.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-3 fade-up-3">
            <Link to="/signup">
              <Button size="lg" className="rounded-full h-12 px-7 btn-glow" data-testid="hero-cta-signup">
                Démarrer gratuitement<ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a href="#features">
              <Button size="lg" variant="outline" className="rounded-full h-12 px-7" data-testid="hero-cta-features">
                Découvrir la plateforme
              </Button>
            </a>
          </div>

          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground fade-up-4">
            <div className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-primary" /> Sans carte bancaire</div>
            <div className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-primary" /> Mise en route en 2 min</div>
            <div className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> RGPD · Hébergement européen</div>
          </div>

          {/* Hero dashboard preview */}
          <div className="relative mt-20 fade-up-4">
            <div className="absolute inset-x-10 -top-8 h-40 bg-gradient-to-b from-primary/30 via-violet-500/20 to-transparent blur-3xl" />
            <div className="relative ring-premium rounded-xl overflow-hidden border border-border bg-card mx-auto max-w-5xl">
              <div className="h-9 border-b border-border bg-muted/40 flex items-center gap-1.5 px-4">
                <div className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
                <div className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
                <div className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
                <div className="ml-4 text-xs text-muted-foreground font-mono">localpulse.fr/dashboard</div>
              </div>
              <div className="p-6 sm:p-8 grid grid-cols-12 gap-4">
                <div className="col-span-12 sm:col-span-4 border border-border rounded-lg p-5 bg-gradient-to-br from-primary/10 to-transparent">
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Score de visibilité</p>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-5xl font-black font-display text-gradient">87</span>
                    <span className="text-sm text-muted-foreground">/100</span>
                  </div>
                  <div className="mt-3 flex items-center gap-1 text-xs text-emerald-500"><TrendingUp className="h-3 w-3" />+24 cette semaine</div>
                </div>
                <div className="col-span-6 sm:col-span-4 border border-border rounded-lg p-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Référencement local</p>
                  <div className="mt-2 text-3xl font-black font-display">92</div>
                  <div className="h-1 mt-3 bg-muted rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-primary to-violet-500 rounded-full" style={{width:"92%"}}/></div>
                </div>
                <div className="col-span-6 sm:col-span-4 border border-border rounded-lg p-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Engagement social</p>
                  <div className="mt-2 text-3xl font-black font-display">78</div>
                  <div className="h-1 mt-3 bg-muted rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-primary to-violet-500 rounded-full" style={{width:"78%"}}/></div>
                </div>
                <div className="col-span-12 border border-border rounded-lg p-5">
                  <div className="flex justify-between items-center mb-3">
                    <p className="text-sm font-semibold">Croissance hebdomadaire</p>
                    <span className="text-xs text-emerald-500 font-medium">+38% ce mois</span>
                  </div>
                  <div className="h-20 flex items-end gap-1.5">
                    {[35, 42, 40, 55, 60, 70, 87].map((v, i) => (
                      <div key={i} className="flex-1 rounded-sm bg-gradient-to-t from-primary/60 to-violet-500/80" style={{ height: `${v}%` }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Logos / sectors marquee */}
      <section className="border-y border-border py-10 bg-muted/30 overflow-hidden">
        <p className="text-center text-xs uppercase tracking-[0.25em] font-bold text-muted-foreground mb-6">
          La plateforme des commerces qui grandissent
        </p>
        <div className="relative">
          <div className="flex gap-12 marquee-track w-max">
            {[...SECTORS, ...SECTORS].map((s, i) => (
              <span key={i} className="text-2xl sm:text-3xl font-display font-black text-muted-foreground/40 hover:text-foreground transition-colors whitespace-nowrap">
                {s}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 lg:py-32 border-b border-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { label: "Commerces accompagnés", value: "Multi-secteurs", hint: "Restaurants · salons · spas · cafés" },
            { label: "Couverture", value: "France entière", hint: "Paris, Lyon, Bordeaux, Marseille…" },
            { label: "Publications", value: "Planifiées", hint: "Calendrier éditorial intégré" },
            { label: "Note client", value: "★★★★★", hint: "Satisfaction · plans payants" },
          ].map((s, i) => (
            <div key={i} className="fade-up" style={{ animationDelay: `${i * 0.1}s` }}>
              <div className="text-3xl sm:text-4xl font-black font-display text-gradient">
                {s.value}
              </div>
              <p className="mt-2 text-sm font-semibold">{s.label}</p>
              <p className="mt-1 text-xs text-muted-foreground">{s.hint}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="py-24 lg:py-32 border-b border-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-primary mb-4">Plateforme</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
              Tout ce qu'il faut<br />pour <span className="font-serif-italic font-normal">grandir.</span>
            </h2>
            <p className="mt-5 text-lg text-muted-foreground max-w-xl">Une seule plateforme pour piloter votre présence en ligne, vos contenus et votre réputation. Pensée pour les indépendants exigeants.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <div key={f.title}
                className="group card-hover relative border border-border rounded-xl p-7 bg-card overflow-hidden"
                data-testid={`feature-${f.title}`}
                style={{ animationDelay: `${i * 0.05}s` }}>
                <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-gradient-to-br from-primary/20 to-violet-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-2xl" />
                <div className="relative h-11 w-11 rounded-lg bg-gradient-to-br from-primary/15 to-violet-500/15 flex items-center justify-center mb-5 ring-1 ring-border group-hover:ring-primary/40 transition">
                  <f.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-xl font-bold tracking-tight">{f.title}</h3>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Persuasion / use cases */}
      <section className="py-24 lg:py-32 border-b border-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-primary mb-4">Pourquoi maintenant</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">
                Vos concurrents sont déjà<br /><span className="font-serif-italic font-normal">recommandés.</span>
              </h2>
              <p className="mt-5 text-muted-foreground text-lg leading-relaxed">
                90% des décisions d'achat locales commencent en ligne. Si votre fiche n'est pas trouvée, vous perdez chaque jour des clients —
                <em className="font-serif-italic"> sans même le savoir</em>. Local Pulse rend votre commerce visible, partout où ça compte.
              </p>
              <ul className="mt-7 space-y-3">
                {[
                  "Audit de visibilité en moins de 60 secondes",
                  "Plan d'action priorisé, étape par étape",
                  "Indicateurs concrets, sans jargon",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm">
                    <span className="mt-0.5 h-5 w-5 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                      <Check className="h-3 w-3 text-primary" />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
              <Link to="/signup" className="inline-block mt-8">
                <Button className="rounded-full btn-glow h-11 px-6">Reprendre la main<ArrowRight className="ml-2 h-4 w-4" /></Button>
              </Link>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 gap-4">
              {[{ img: RESTAURANT, t: "Restaurants & Cafés", d: "+38% de réservations en 60 jours", h: "h-72" },
                { img: SALON, t: "Salons & Spas", d: "Top 3 Google Maps en 6 semaines", h: "h-56" }].map((c, i) => (
                <div key={c.t} className={`relative rounded-xl overflow-hidden border border-border group ${i === 0 ? "row-span-2" : ""}`}>
                  <img src={c.img} alt={c.t} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-5 text-white">
                    <h3 className="font-bold">{c.t}</h3>
                    <p className="text-sm text-white/80 mt-1">{c.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24 lg:py-32 border-b border-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-primary mb-4">Clients</p>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tighter max-w-2xl">
            Ils dominent <span className="font-serif-italic font-normal">leur quartier.</span>
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { img: TESTI_SOPHIE, name: "Sophie L.", role: "Bistrot · Paris 1er",
                quote: "Notre fiche Google est remontée dans les premiers résultats sur notre quartier en quelques semaines. Le brunch dominical affiche complet plus tôt.", metric: "Réservations en hausse" },
              { img: TESTI_KARIM, name: "Karim B.", role: "Food truck · Marseille",
                quote: "On a gagné en abonnés sur Instagram et nos commandes Uber Eats ont suivi. Local Pulse comprend vraiment le marché français — pas un outil traduit à la va-vite.", metric: "Abonnés en croissance" },
              { img: TESTI_CAMILLE, name: "Camille R.", role: "Salon de coiffure · Bordeaux",
                quote: "Avant Local Pulse, je perdais mes soirées à répondre aux avis et planifier mes posts. Maintenant je passe quelques minutes par semaine et je suis plus régulière que jamais.", metric: "Temps gagné chaque semaine" },
              { img: TESTI_ANTOINE, name: "Antoine M.", role: "Spa · Aix-les-Bains",
                quote: "Le diagnostic m'a montré exactement ce qui clochait sur ma fiche. Quelques mois plus tard, les nouveaux avis 5 étoiles arrivent régulièrement et le calendrier tourne tout seul.", metric: "Nouveaux avis réguliers" },
            ].map((t) => (
              <div key={t.name} className="relative card-hover border border-border rounded-2xl p-8 bg-card" data-testid={`testimonial-${t.name}`}>
                <Quote className="absolute -top-3 left-6 h-8 w-8 text-primary fill-primary bg-background" />
                <div className="flex gap-0.5 text-amber-500 mb-5">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-amber-500" />)}</div>
                <p className="text-base leading-relaxed font-medium">"{t.quote}"</p>
                <div className="mt-6 pt-5 border-t border-border flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <img src={t.img} className="h-11 w-11 rounded-full object-cover ring-2 ring-primary/20" alt={t.name} />
                    <div>
                      <div className="font-bold text-sm">{t.name}</div>
                      <div className="text-xs text-muted-foreground">{t.role}</div>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-emerald-600 bg-emerald-500/10 px-2.5 py-1 rounded-full">{t.metric}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 lg:py-32 border-b border-border relative overflow-hidden">
        <div className="absolute inset-0 conic-bg opacity-40" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-primary mb-4">Tarifs</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
              Sans engagement.<br />
              <span className="font-serif-italic font-normal">Annulable en un clic.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { id: "free", name: "Free", price: "0€", desc: "Pour découvrir la plateforme",
                features: ["3 contenus rédigés / mois", "Audit de visibilité basique", "1 profil business"] },
              { id: "pro", name: "Pro", price: "29€", priceAnnual: "23€", saving: "−68€/an", desc: "Pour les commerces qui grandissent", featured: true,
                features: ["Contenus illimités", "Audit complet & concurrents", "Calendrier éditorial", "3 profils business", "Support email prioritaire"] },
              { id: "business", name: "Business", price: "79€", priceAnnual: "63€", saving: "−190€/an", desc: "Pour les multi-établissements",
                features: ["Tout Pro inclus", "Comparaison concurrents avancée", "API & multi-utilisateurs", "10 profils business", "Support dédié"] },
            ].map((p) => (
              <div key={p.id}
                className={`relative rounded-2xl p-8 transition-all ${
                  p.featured ? "bg-gradient-to-br from-primary/5 to-violet-500/5 border-2 border-primary/40 ring-premium" : "bg-card border border-border card-hover"
                }`}
                data-testid={`pricing-${p.id}`}>
                {p.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-primary to-violet-500 text-white text-[10px] font-bold uppercase tracking-wider">
                    Le plus choisi
                  </div>
                )}
                <h3 className="text-2xl font-black tracking-tight">{p.name}</h3>
                <p className="text-sm text-muted-foreground">{p.desc}</p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className={`text-5xl font-black font-display ${p.featured ? "text-gradient" : ""}`}>{p.priceAnnual || p.price}</span>
                  <span className="text-sm text-muted-foreground">/mois</span>
                </div>
                {p.saving && (
                  <p className="text-xs text-muted-foreground mt-1.5">
                    En annuel · <span className="text-emerald-600 font-semibold">{p.saving}</span> vs mensuel ({p.price}/mois)
                  </p>
                )}
                {!p.saving && p.id !== "free" && <p className="text-xs text-muted-foreground mt-1.5">Facturé chaque mois</p>}
                {p.id === "free" && <p className="text-xs text-muted-foreground mt-1.5">Gratuit à vie · sans carte</p>}
                <ul className="mt-7 space-y-3 text-sm">
                  {p.features.map(f => (
                    <li key={f} className="flex items-start gap-2.5">
                      <span className="mt-0.5 h-4 w-4 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                        <Check className="h-2.5 w-2.5 text-primary" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/signup" className="block mt-8">
                  <Button className={`w-full rounded-full h-11 ${p.featured ? "btn-glow" : ""}`} variant={p.featured ? "default" : "outline"} data-testid={`pricing-cta-${p.id}`}>
                    {p.id === "free" ? "Démarrer gratuitement" : `Choisir ${p.name}`}
                  </Button>
                </Link>
                {p.id !== "free" && (
                  <p className="text-[11px] text-center text-muted-foreground mt-3">7 jours d'essai gratuit · sans engagement</p>
                )}
              </div>
            ))}
          </div>
          {/* Guarantee row */}
          <div className="mt-12 max-w-3xl mx-auto border border-border rounded-2xl p-6 bg-card flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Check className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-sm font-semibold">Essai gratuit · 7 jours</p>
                <p className="text-xs text-muted-foreground">Annulez en un clic, sans justification, sans frais.</p>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              Paiement sécurisé via <span className="font-semibold text-foreground">Stripe</span> · RGPD
            </div>
          </div>
        </div>
      </section>
      <section id="faq" className="py-24 lg:py-32 border-b border-border">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-primary mb-4">FAQ</p>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tighter mb-10">Questions fréquentes</h2>
          <Accordion type="single" collapsible className="w-full">
            {[
              { q: "Comment Local Pulse améliore ma visibilité ?", a: "Nous auditons votre fiche Google Business, votre site et vos réseaux, puis nous établissons un plan d'action priorisé : optimisations, contenus à publier, créneaux idéaux. Le tout suivi en temps réel sur votre tableau de bord." },
              { q: "Faut-il une carte bancaire pour démarrer ?", a: "Non. Le plan Free est gratuit à vie, sans engagement, sans carte bancaire." },
              { q: "Puis-je publier directement vers Instagram et TikTok ?", a: "Le calendrier prépare et planifie vos publications. La publication directe (API native) est progressivement déployée pour les plans Pro et Business." },
              { q: "Le contenu est-il vraiment en français professionnel ?", a: "Oui. Le studio de contenu est calibré pour le marché français : ton, vocabulaire, références culturelles. Adapté à chaque secteur — restaurant, salon, café, commerce." },
              { q: "Puis-je annuler à tout moment ?", a: "Oui, sans engagement. Annulation en un clic depuis votre espace facturation." },
              { q: "Mes données sont-elles en sécurité ?", a: "Hébergement européen, chiffrement bout en bout, conformité RGPD garantie. Vos données restent les vôtres et ne sont jamais revendues." },
            ].map((f, i) => (
              <AccordionItem key={i} value={`q${i}`} className="border-border" data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left text-base font-semibold hover:text-primary py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-40 relative overflow-hidden">
        <div className="aurora opacity-60" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 glass rounded-full text-xs font-medium mb-7">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" />
            Hébergement européen · RGPD
          </div>
          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter">
            Prêt à devenir<br />
            <span className="text-gradient">incontournable</span>
            <span className="font-serif-italic font-normal"> ?</span>
          </h2>
          <p className="text-muted-foreground mt-7 text-lg sm:text-xl max-w-xl mx-auto">
            Rejoignez les commerces qui dominent leur quartier — sur Google, Instagram, TikTok et au-delà.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/signup">
              <Button size="lg" className="rounded-full h-12 px-8 btn-glow text-base" data-testid="cta-final-signup">
                Créer mon compte<ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="rounded-full h-12 px-8 text-base" data-testid="cta-final-login">
                Se connecter
              </Button>
            </Link>
          </div>
          <p className="mt-6 text-xs text-muted-foreground">Aucune carte bancaire requise · Mise en route en 2 minutes</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 bg-muted/20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <Logo size={20} />
            <div className="flex items-center gap-5 text-xs text-muted-foreground flex-wrap">
              <a href="#features" className="hover:text-foreground transition-colors">Plateforme</a>
              <a href="#pricing" className="hover:text-foreground transition-colors">Tarifs</a>
              <a href="#faq" className="hover:text-foreground transition-colors">FAQ</a>
              <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors">Confidentialité</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">CGU</Link>
              <span className="hidden sm:inline-flex items-center gap-1.5"><Globe className="h-3 w-3"/> Français</span>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-border flex flex-wrap justify-between gap-3 text-xs text-muted-foreground">
            <p>© 2026 Local Pulse SAS · 32 rue du Faubourg Saint-Denis · 75010 Paris</p>
            <p>RGPD · Hébergement européen · Paiement sécurisé Stripe</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
